import React, { useState, useRef } from "react";
import "./style.css";
import ReCAPTCHA from "react-google-recaptcha";
import Popup from "reactjs-popup";
import "reactjs-popup/dist/index.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { contactFromHomeAPI } from "../../redux/slices/ContactFormApiSlice";
import contactSectionComponent from "../../data/contactSectionComponentData"; // Import your dynamic data
import { useTranslation } from "react-i18next";
import ActionButton from "../ActionButton";

const ContactSection = () => {
  const dispatch = useDispatch();

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null); // State to hold the actual file object

  // Corrected the selector to safely access `loading`
  const loading = useSelector(
    (state) => state.contactFormApiSlice?.loading || false
  );

  const [data, setData] = useState({
    full_name: "",
    email: "",
    phone_number: "",
    details: "", // This will store the file name for display
    about_project: "",
    services: [], // This will store the selected service objects from react-select
  });

  const isFormComplete = () => {
    // Check if all required fields have values

    return (
      data.full_name.trim() !== "" &&
      data.email.trim() !== "" &&
      data.phone_number.trim() !== "" &&
      data.about_project.trim() !== "" &&
      data.services.length > 0
      // If 'details' (file attachment) is optional, remove it from this check.
      // If it's required, ensure `attachedFile` is not null.
      // && attachedFile !== null // Uncomment if file is mandatory
    );
  };

  const handleServicesChange = (selected) => {
    setData((prevData) => ({
      ...prevData,
      services: selected,
    }));
  };

  const handleChange = (e) => {
    const { id, value, type, files } = e.target; // Destructure files for file input

    if (type === "file") {
      const file = files[0];
      setAttachedFile(file); // Store the actual file object
      setData((prevData) => ({
        ...prevData,
        details: file ? file.name : "", // Store just the file name in `data.details` for display
      }));
    } else {
      setData((prevData) => ({
        ...prevData,
        [id]: value,
      }));
    }
  };

  const recaptchaRef = useRef(null);
  const [captchaValue, setCaptchaValue] = useState(null);

  const getRecaptchaValue = (value) => {
    setCaptchaValue(value);
  };

  const handleRecaptchaExpired = () => {
    setCaptchaValue(null);
    if (recaptchaRef.current) {
      recaptchaRef.current.reset();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic client-side validation
    if (!isFormComplete()) {
      toast.error("Please fill in all required fields.");
      return;
    }

    const recaptchaToken = recaptchaRef.current
      ? recaptchaRef.current.getValue()
      : null;

    if (!recaptchaToken) {
      toast.error("Please complete the reCAPTCHA.");
      return;
    }

    // Prepare data for API submission using FormData
    const formDataToSubmit = new FormData();
    formDataToSubmit.append("full_name", data.full_name);
    formDataToSubmit.append("email", data.email);
    formDataToSubmit.append("phone_number", data.phone_number);
    formDataToSubmit.append("about_project", data.about_project);
    formDataToSubmit.append("recaptcha_token", recaptchaToken); // Send reCAPTCHA token

    // Append services. Since `react-select` gives objects, extract just the values
    data.services.forEach((service) => {
      formDataToSubmit.append("services[]", service.value); // Assuming 'value' is what your API expects
    });

    // Append the actual file if it exists
    if (attachedFile) {
      formDataToSubmit.append("details", attachedFile); // 'details' should match your API's expected field name for the file
    }

    const res = await dispatch(contactFromHomeAPI(formDataToSubmit));

    if (res.type === "contactFromHomeAPI/fulfilled") {
      // toast.success(res?.payload?.message || "Your query submitted successfully!");
      setIsPopupOpen(true); // Open the popup on success
      // Reset form fields after successful submission
      setData({
        full_name: "",
        email: "",
        phone_number: "",
        details: "",
        about_project: "",
        services: [],
      });
      setAttachedFile(null); // Clear the attached file
      recaptchaRef.current?.reset(); // Reset reCAPTCHA
      setCaptchaValue(null); // Clear captcha value state
    } else if (res.type === "contactFromHomeAPI/rejected") {
      // Check for a specific error message from the payload, or a generic one
      toast.error(
        res.payload?.errors ||
          res.error?.message ||
          "Sorry, something went wrong!"
      );
    } else {
      toast.error("Sorry, something went wrong!"); // Fallback error
    }
  };

  // ARABIC TRANSLATION

  const { t } = useTranslation("contactSection");
  const heading = t("heading", { returnObjects: true });
  const skilledTalent = t("skilledTalent", { returnObjects: true });
  const swiftTurnaround = t("swiftTurnaround", { returnObjects: true });
  const costEffective = t("costEffective", { returnObjects: true });
  const innovationDriven = t("innovationDriven", { returnObjects: true });
  const contactEmails = t("contactEmails", { returnObjects: true });
  const contactPhones = t("contactPhones", { returnObjects: true });
  const servicesOptions = t("servicesOptions", { returnObjects: true });
  const contactFormLabels = t("formLabels", { returnObjects: true });

  return (
    <>
      <ToastContainer
        hideProgressBar={true}
        position="top-right"
        autoClose={5000}
      />
      <div className="contact-section-box height-100-vh" id="contactForm">
        <div className="container">
          <div className="row">
            <div className="col-md-6">
              <div className="contact-left-image">
                <img
                  src="/images/contact-left-bg.png"
                  alt="Contact Background"
                />
              </div>

              <div className="contact-text-side center-box-contract-section flex-start-box">
                <div className="contact-text-box">
                  <h1
                    dangerouslySetInnerHTML={{
                      __html: heading,
                    }}
                  />
                </div>

                <div className="container-fluid p-0">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="skilled-contact-section">
                        <h2>{skilledTalent.title}</h2>
                        <p className="common-text">
                          {skilledTalent.description}
                        </p>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="skilled-contact-section">
                        <h2>{swiftTurnaround.title}</h2>
                        <p className="common-text">
                          {swiftTurnaround.description}
                        </p>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="skilled-contact-section">
                        <h2>{costEffective.title}</h2>
                        <p className="common-text">
                          {costEffective.description}
                        </p>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="skilled-contact-section">
                        <h2>{innovationDriven.title}</h2>
                        <p className="common-text">
                          {innovationDriven.description}
                        </p>
                      </div>
                    </div>

                    {contactEmails?.map((item, index) => (
                      <div
                        key={`email-${index}`}
                        className="col-xl-6 col-lg-6 col-md-12 col-sm-12 mt-4"
                      >
                        <a href={item.href} className="contact-mail-to-text">
                          <img src={item.icon} alt="" /> {item.text}
                        </a>
                      </div>
                    ))}

                    {contactPhones.map((item, index) => (
                      <div
                        key={`phone-${index}`}
                        className="col-xl-6 col-lg-6 col-md-12 col-sm-12 mt-4"
                      >
                        <a href={item.href} className="contact-mail-to-text">
                          <img src={item.icon} alt="" /> {item.text}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="contact-left-image bottom-image-contact bottom-contact-image">
                <img
                  src="/images/contact-bg-right.png"
                  alt="Contact Background"
                />
              </div>
            </div>

            <div className="col-md-6 flex-contact-form">
              <div className="contact-side-form">
                <div className="container p-0">
                  <form
                    className="row contact-side-form-box"
                    onSubmit={handleSubmit}
                    id="footer-contact-us-form-click"
                  >
                    <div className="col-md-6">
                      <div className="form-floating">
                        <input
                          required
                          type="text"
                          placeholder={contactFormLabels.fullName}
                          className="form-control"
                          id="full_name"
                          onChange={handleChange}
                          value={data.full_name}
                        />
                        <label htmlFor="full_name">
                          {contactFormLabels.fullName}{" "}
                          <span style={{ color: "red" }}>
                            {contactFormLabels.requiredSymbol}
                          </span>
                        </label>
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-floating">
                        <input
                          required
                          type="email"
                          placeholder={contactFormLabels.email}
                          className="form-control"
                          id="email"
                          onChange={handleChange}
                          value={data.email}
                        />
                        <label htmlFor="email">
                          {contactFormLabels.email}{" "}
                          <span style={{ color: "red" }}>
                            {contactFormLabels.requiredSymbol}
                          </span>
                        </label>
                      </div>
                    </div>

                    <div className="col-md-12">
                      <div className="form-floating">
                        <input
                          required
                          type="number"
                          placeholder={contactFormLabels.phoneNumber}
                          className="form-control"
                          id="phone_number"
                          onChange={handleChange}
                          value={data.phone_number}
                        />
                        <label htmlFor="phone_number">
                          {contactFormLabels.phoneNumber}{" "}
                          <span style={{ color: "red" }}>
                            {contactFormLabels.requiredSymbol}
                          </span>
                        </label>
                      </div>
                    </div>

                    <div className="col-md-12">
                      <div className="form-floating">
                        <Select
                          styles={{
                            control: (baseStyles) => ({
                              ...baseStyles,
                              borderRadius: "0 !important",
                              backgroundColor: "transparent",
                              border: "none !important",
                              borderBottom: "1.12px solid #FFFFFF !important",
                              boxShadow: "none !important",
                              color: "#FFFFFF",
                              fontSize: "16px",
                              padding: "16px 0px 0px 0px",
                            }),
                            placeholder: (baseStyles) => ({
                              ...baseStyles,
                              color: "#fff",
                              fontSize: "16px",
                            }),
                            menu: (baseStyles) => ({
                              ...baseStyles,
                              backgroundColor: "#333333",
                            }),
                            singleValue: (baseStyles) => ({
                              ...baseStyles,
                              color: "#fff", // Color of selected value
                            }),
                            multiValue: (baseStyles) => ({
                              ...baseStyles,
                              backgroundColor: "#80b927", // Color of selected tag
                            }),
                            multiValueLabel: (baseStyles) => ({
                              ...baseStyles,
                              color: "#fff", // Color of selected tag text
                            }),
                            multiValueRemove: (baseStyles) => ({
                              ...baseStyles,
                              color: "#fff", // Color of selected tag remove icon
                              ":hover": {
                                backgroundColor: "#6a9722",
                                color: "white",
                              },
                            }),
                          }}
                          theme={(theme) => ({
                            ...theme,
                            colors: {
                              ...theme.colors,
                              primary25: "#80b927", // Color on hover
                              primary: "#80b927", // Color when selected/focused
                              neutral80: "#FFFFFF", // Color of the text input for selected options
                            },
                          })}
                          isMulti
                          value={data.services}
                          onChange={handleServicesChange}
                          options={servicesOptions} // Dynamic services options
                          placeholder={contactFormLabels.services}
                          required
                        />
                      </div>
                    </div>

                    <div className="col-md-12">
                      <div className="form-floating">
                        <input
                          required
                          type="text"
                          placeholder={contactFormLabels.aboutProject}
                          className="form-control"
                          id="about_project"
                          onChange={handleChange}
                          value={data.about_project}
                        />
                        <label htmlFor="about_project">
                          {contactFormLabels.aboutProject}{" "}
                          <span style={{ color: "red" }}>
                            {contactFormLabels.requiredSymbol}
                          </span>
                        </label>
                      </div>
                    </div>

                    <div className="col-md-12">
                      <div className="form-floating">
                        <div
                          style={{
                            height: "60px",
                            borderBottom: "1px solid #fff",
                            display: "flex",
                            alignItems: "center",
                            padding: "0px",
                            cursor: "pointer",
                            color: data.details ? "#FFFFFF" : "#BABABA",
                          }}
                          onClick={() =>
                            document.getElementById("fileInput").click()
                          }
                        >
                          <span style={{ flex: 1 }}>
                            {data.details || contactFormLabels.attachFile}
                          </span>
                          <span style={{ color: "#BABABA" }}>
                            <i className="fa-solid fa-paperclip"></i>
                          </span>
                        </div>
                        <input
                          type="file"
                          id="fileInput"
                          onChange={handleChange} // Use the unified handleChange
                          style={{
                            display: "none",
                            position: "absolute",
                            pointerEvents: "none",
                          }}
                        />
                      </div>
                    </div>

                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={"6LduktkqAAAAAAG-TJxQPKptt1p5F4l_FYqYqNgv"} // Dynamic site key
                      onChange={getRecaptchaValue}
                    />

                    <div className="col-md-5 mt-3">
                      {/* <button
                        type="submit"
                        disabled={loading || !isFormComplete() || !captchaValue}
                        className="btn btn-primary"
                      >
                        {loading ? "Loading..." : contactFormLabels.submitButton}
                      </button> */}

                      <ActionButton
                        text={contactFormLabels.submitButton}
                        type="submit"
                        disabled={loading || !isFormComplete() || !captchaValue}
                        hideIcon
                      />
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Popup
        open={isPopupOpen}
        position="center center"
        closeOnDocumentClick={false}
        onClose={() => setIsPopupOpen(false)}
      >
        <div
          className="position-relative"
          style={{ backgroundColor: "rgb(242 242 242)", borderRadius: "20px" }}
        >
          <button
            onClick={() => setIsPopupOpen(false)}
            className="cross-btn-close"
          >
            <i className="fa-solid fa-xmark"></i>
          </button>

          <video
            width="100%"
            height="100%"
            autoPlay
            muted
            onEnded={() => setIsPopupOpen(false)}
          >
            <source src={"/videos/thankyou-vedio.mp4"} type="video/mp4" />{" "}
            {/* Dynamic video source */}
            Your browser does not support the video tag.
          </video>

          <div className="header-link-green d-flex justify-content-center pb-5">
            <a
              href={"https://calendly.com/bitsclan-it/30min?month=2025-01"} // Dynamic Calendly link
              target="_blank"
              rel="noopener noreferrer"
            >
              Let's Talk
            </a>
          </div>
        </div>
      </Popup>
    </>
  );
};

export default ContactSection;
